'use client'

import type { PropsChainIdMarketId } from '@morpho-org/ui-v6/types'

import { InterfaceChainUtils } from '@morpho-org/dapp-core'
import { DataList } from '@morpho-org/ui-v6/client'
import { ExternalLink } from '@morpho-org/ui-v6/server'
import { formatters } from '@morpho-org/ui-v6/utils'

import type { MarketIrm } from '@/lib/procedures/marketIrm.contract'
import type { MarketState } from '@/lib/procedures/marketState.contract'

import { SllBadge } from '@/components/common/SllBadge'
import { TextAddress } from '@/components/common/TextAddress'
import { PUBLIC_ALLOCATOR_DOCS_URL } from '@/config/external-urls'

export type UtilizationTableClientProps = PropsChainIdMarketId &
  Pick<MarketIrm, 'irmAddress' | 'utilization'> &
  Pick<MarketState, 'borrowToTargetUsd'>

const percentageFormatter = formatters.percent.digits(2).default('-').unit('%')
const usdFormatter = formatters.short.digits(2).default('-').unit('$')

const UtilizationTableClient = ({
  chainId,
  marketId,
  irmAddress,
  utilization,
  borrowToTargetUsd
}: UtilizationTableClientProps) => {
  const explorerName = InterfaceChainUtils.tryGetChain(chainId)?.blockExplorers?.default.name

  const currentUtilizationFormatted = percentageFormatter.of(utilization)

  // Includes shared (reallocatable) liquidity, so it can be positive above target
  // utilization — render as-is, don't gate it on utilization.
  const borrowToTargetFormatted = usdFormatter.of(borrowToTargetUsd)

  return (
    <DataList.Root>
      <DataList.Column>
        <DataList.Line className="lg:h-auto lg:flex-1">
          <DataList.LeftItem label="Target utilization" />
          <DataList.RightItem label="90%" />
        </DataList.Line>
        <DataList.Line className="lg:h-auto lg:flex-1">
          <DataList.LeftItem label="Current utilization" />
          <DataList.RightItem label={currentUtilizationFormatted} />
        </DataList.Line>
        <DataList.Line className="lg:h-auto lg:flex-1">
          <DataList.LeftItem
            label="Borrow amount to target utilization"
            tooltip={
              <span>
                Liquidity left to borrow before the market reaches target utilization, including
                both market and Public Allocator liquidity.{' '}
                <ExternalLink href={PUBLIC_ALLOCATOR_DOCS_URL} underlined>
                  Learn more
                </ExternalLink>
              </span>
            }
          />
          <DataList.RightItem className="gap-xs">
            <SllBadge chainId={chainId} marketId={marketId} source="borrow_to_target" />
            <span className="text-foreground text-xs whitespace-nowrap">
              {borrowToTargetFormatted}
            </span>
          </DataList.RightItem>
        </DataList.Line>
        <DataList.Line className="lg:h-auto lg:flex-1">
          <DataList.LeftItem label="Interest rate model" />
          <DataList.RightItem>
            {irmAddress ? (
              <TextAddress
                address={irmAddress}
                targetChainId={chainId}
                toastMessage="Interest rate model address copied to clipboard."
                tooltipMessage="Copy interest rate model address."
                explorerName={explorerName}
                textClassName="text-xs"
                textColorClass="text-foreground"
              />
            ) : (
              <span className="text-foreground text-xs">None</span>
            )}
          </DataList.RightItem>
        </DataList.Line>
      </DataList.Column>
    </DataList.Root>
  )
}

export default UtilizationTableClient
